import { html } from '../lib/html.js';
import { confirm, toast, toastError } from '../lib/ui.js';
import { tabbarFooter } from '../lib/tabs.js';
import { openSpaceSheet, spaceButton } from '../lib/space-ui.js';
import { accessRequests, authorizations, fmtDateTime } from '../services/authorizations.js';
import { CATEGORY, CATEGORY_KEYS, categoryOf } from '../services/vehicles.js';
import { me } from '../services/me.js';
import { Page, go } from './base.js';
import { vehicleRow } from './workshop.js';

/**
 * Veículos (aba do espaço Empresa): os veículos que os donos liberaram para a empresa.
 * Filtro por linha (só se houver mais de uma) e grupos pelo fim do acesso: "Acesso termina hoje" e "Próximos dias".
 * No topo, "Pedidos pela placa": as 5 últimas placas que a empresa pediu, com busca (a partir de 2 caracteres).
 * Cada placa é uma linha que abre (acordeão); os pedidos dela só são buscados quando a linha é aberta.
 * Tocar abre o veículo (histórico, próximas revisões e Registrar serviço).
 */
class WorkshopVehiclesPage extends Page {
  template() {
    return html`
      <ion-content class="ion-padding tc-page root-page has-tabbar" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow">
          <div class="top-row"></div>
          <div class="title-row">
            <h1 class="page-title">Veículos</h1>
            <button type="button" class="add-btn biz-add" data-go="/autorizacao" aria-label="Receber veículo"><ion-icon name="tc-qr"></ion-icon></button>
          </div>
          <section class="requests" hidden>
            <p class="group-label">Pedidos pela placa</p>
            <label class="searchbar"><ion-icon name="search-outline" aria-hidden="true"></ion-icon><input type="search" placeholder="Buscar placa ou série pedida" aria-label="Buscar placa pedida" enterkeyhint="search" autocapitalize="characters" autocomplete="off" maxlength="30" /></label>
            <div class="plates"></div>
          </section>
          <div class="filters"></div>
          <div class="body"></div>
        </div>
      </ion-content>
      ${tabbarFooter('vehicles')}`;
  }

  setup() {
    this.filter = 'ALL';
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-action="space"]')) return openSpaceSheet();
      const c = e.target.closest('[data-action="cancel-request"]');
      if (c) return this.cancelRequest(c.dataset.id);
      const p = e.target.closest('[data-action="toggle-plate"]');
      if (p) return this.togglePlate(p.dataset.id);
      const f = e.target.closest('[data-filter]');
      if (f) {
        this.filter = f.dataset.filter;
        return this.render();
      }
      const el = e.target.closest('[data-go]');
      if (el) go(el.dataset.go);
    });
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
    // busca das placas pedidas: consulta o servidor com 2+ caracteres, depois de uma pausa na digitação
    this.$('.requests input').addEventListener('input', (e) => {
      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9 -]/g, '');
      const term = e.target.value.replace(/[^A-Z0-9]/g, '');
      clearTimeout(this.searchTimer);
      if (term.length === 1 || term === this.term) return;
      this.searchTimer = setTimeout(() => this.loadPlates(term, { skeleton: true }), SEARCH_DELAY_MS);
    });
    this.addEventListener('ionViewWillLeave', () => clearTimeout(this.searchTimer));
  }

  onEnter() {
    this.$('tc-tabbar')?.refresh();
    this.$('.top-row').innerHTML = spaceButton().value ?? '';
    this.term = '';
    this.openPlate = null;
    this.history = {};
    this.$('.requests input').value = '';
    this.load();
  }

  async load() {
    const w = me.current?.workshop;
    if (w && w.status !== 'APPROVED') {
      this.items = [];
      this.blocked = true;
      return this.render();
    }
    this.blocked = false;
    if (!this.items) this.$('.body').innerHTML = '<div class="center-small"><ion-spinner></ion-spinner></div>';
    this.loadPlates(this.term); // extra: se falhar, a lista de veículos continua
    try {
      this.items = (await authorizations.workshopVehicles()).items;
      this.error = null;
    } catch (err) {
      this.error = err.message;
      if (this.items) toastError(err.message);
    }
    this.render();
  }

  /** Placas pedidas pela empresa: as 5 últimas ou, com busca, as que têm o texto digitado. */
  async loadPlates(term = '', { skeleton = false } = {}) {
    this.term = term;
    // busca digitada: linhas-esqueleto no lugar da lista enquanto o servidor responde
    this.searching = skeleton;
    if (skeleton) this.renderPlates();
    try {
      const { items } = await accessRequests.sent(term);
      if (term !== this.term) return; // chegou depois de outra busca
      this.plates = items;
      // a linha aberta continua aberta (com os pedidos recarregados) se o veículo ainda está na lista
      const open = this.openPlate && items.find((x) => x.id === this.openPlate);
      this.openPlate = open ? open.id : null;
      if (open) this.loadHistory(open.id);
    } catch {
      if (term !== this.term) return;
      this.plates = this.plates ?? [];
    }
    this.searching = false;
    this.renderPlates();
  }

  togglePlate(id) {
    this.openPlate = this.openPlate === id ? null : id;
    this.renderPlates();
    if (this.openPlate) this.loadHistory(id);
  }

  /** Pedidos de uma placa: só são buscados quando a linha é aberta. */
  async loadHistory(id) {
    if (!this.history[id]?.items) this.history[id] = { loading: true };
    this.renderPlates();
    try {
      this.history[id] = { items: (await accessRequests.history(id)).items };
    } catch (err) {
      this.history[id] = { error: err.message };
    }
    this.renderPlates();
  }

  renderPlates() {
    const section = this.$('.requests');
    const plates = this.plates ?? [];
    // sem nenhum pedido feito, o bloco (e a busca) nem aparece
    section.hidden = this.blocked || (!plates.length && !this.term && !this.searching);
    if (section.hidden) return;
    if (this.searching) {
      this.$('.plates').innerHTML = html`<div class="panel vlist" aria-busy="true" aria-label="Buscando">${skeletonRows(SKELETON_PLATES, { icon: true })}</div>`.value;
      return;
    }
    this.$('.plates').innerHTML = plates.length
      ? html`<div class="panel vlist">${plates.map((p) => plateRow(p, this.openPlate === p.id ? this.history[p.id] : null))}</div>`.value
      : html`<div class="panel"><p class="panel-text">Nenhum pedido para "${this.term}".</p></div>`.value;
  }

  async cancelRequest(id) {
    const r = Object.values(this.history).flatMap((h) => h.items ?? []).find((x) => x.id === id);
    if (!(await confirm({ header: 'Cancelar pedido?', message: `O dono de ${r?.label ?? 'este veículo'} deixa de ver o pedido.`, confirmText: 'Cancelar pedido', cancelText: 'Voltar', destructive: true }))) return;
    try {
      await accessRequests.cancel(id);
      toast('Pedido cancelado.');
    } catch (err) {
      toastError(err.message);
    }
    this.loadPlates(this.term);
  }

  render() {
    const items = this.items ?? [];
    const counts = Object.fromEntries(CATEGORY_KEYS.map((k) => [k, items.filter((x) => categoryOf(x.vehicle).key === k).length]));
    const lines = CATEGORY_KEYS.filter((k) => counts[k]);
    if (lines.length < 2) this.filter = 'ALL';
    const chip = (key, label, icon = '') =>
      html`<button type="button" class="chip ${this.filter === key ? 'on' : ''}" data-filter="${key}" aria-pressed="${this.filter === key ? 'true' : 'false'}">${icon ? html`<ion-icon class="cat-color-${key}" name="${icon}" aria-hidden="true"></ion-icon>` : ''}${label}</button>`;
    this.$('.filters').innerHTML =
      lines.length > 1 ? html`<div class="chip-row" role="group" aria-label="Filtrar por linha">${chip('ALL', `Todos · ${items.length}`)}${lines.map((k) => chip(k, `${CATEGORY[k].short} ${counts[k]}`, CATEGORY[k].icon))}</div>`.value : '';

    this.renderPlates();
    const body = this.$('.body');
    if (this.blocked) {
      body.innerHTML = html`<div class="panel notice" style="margin-top:16px"><span class="row-icon warning"><ion-icon name="time-outline"></ion-icon></span><div><strong>Empresa ainda não liberada</strong><p>Quando a análise do cadastro terminar, os veículos que os donos liberarem aparecem aqui.</p></div></div>`.value;
      return;
    }
    if (this.error && !this.items) {
      body.innerHTML = html`<div class="panel" style="margin-top:16px"><p class="panel-text">Não foi possível carregar os veículos. Puxe para baixo para tentar de novo.</p></div>`.value;
      return;
    }
    if (!items.length) {
      body.innerHTML = html`
        <div class="panel empty-panel" style="margin-top:16px">
          <div class="empty-badge" style="background:var(--tc-biz-soft);color:var(--tc-biz-text)"><ion-icon name="tc-qr"></ion-icon></div>
          <strong>Nenhum veículo liberado agora</strong>
          <p>Leia o QR ou digite os 6 números que o dono gera no app dele. Sem o dono por perto, peça o acesso pela placa.</p>
          <ion-button expand="block" class="empty-cta biz" data-go="/autorizacao"><ion-icon slot="start" name="tc-qr"></ion-icon>Receber veículo</ion-button>
        </div>`.value;
      return;
    }
    const list = items.filter((x) => this.filter === 'ALL' || categoryOf(x.vehicle).key === this.filter);
    const today = new Date().toDateString();
    const groups = [
      { label: 'Acesso termina hoje', items: list.filter((x) => new Date(x.expiresAt).toDateString() === today) },
      { label: 'Próximos dias', items: list.filter((x) => new Date(x.expiresAt).toDateString() !== today) },
    ].filter((g) => g.items.length);
    body.innerHTML = html`${groups.map((g) => html`<p class="group-label">${g.label}</p><div class="panel vlist">${g.items.map((x) => vehicleRow(x))}</div>`)}`.value;
  }
}

const SEARCH_DELAY_MS = 350;
const SKELETON_PLATES = 3;
const SKELETON_HISTORY = 2;

/** Linhas-esqueleto (carregando): mesmo formato das linhas de verdade, para a lista não pular quando os dados chegam. */
const skeletonRows = (n, { icon = false } = {}) =>
  Array.from({ length: n }, () => html`
    <div class="row sk-row" aria-hidden="true">
      ${icon ? html`<span class="sk sk-icon"></span>` : ''}
      <span class="row-main"><span class="sk sk-title"></span><span class="sk sk-sub"></span></span>
    </div>`);

/** Situação de um pedido em uma linha: o que aconteceu e quando. */
function requestText(r) {
  switch (r.status) {
    case 'PENDING':
      return `Aguardando o dono · até ${fmtDateTime(r.expiresAt)}`;
    case 'ACCEPTED':
      return `Aceito em ${fmtDateTime(r.answeredAt)}${r.durationDays ? ` · ${r.durationDays} ${r.durationDays === 1 ? 'dia' : 'dias'}` : ''}`;
    case 'DECLINED':
      return `Recusado em ${fmtDateTime(r.answeredAt)}`;
    case 'EXPIRED':
      return `Sem resposta: expirou em ${fmtDateTime(r.expiresAt)}`;
    default:
      return `Cancelado em ${fmtDateTime(r.answeredAt)}`;
  }
}

/** Cor do texto da situação na linha aberta: aceito em verde, recusado em vermelho e aguardando em amarelo. */
const REQUEST_TONE = { ACCEPTED: 'success', DECLINED: 'danger', PENDING: 'warning' };
const REQUEST_ICON ={ PENDING: ['time-outline', 'warning'], ACCEPTED: ['checkmark-circle-outline', 'success'], DECLINED: ['close-circle-outline', 'danger'] };

/**
 * Uma placa pedida: linha com a situação do último pedido e a setinha; aberta, mostra os pedidos daquela placa
 * (history: { loading } | { error } | { items }).
 */
function plateRow(p, history) {
  const [icon, tone] = REQUEST_ICON[p.status] ?? ['remove-circle-outline', ''];
  return html`
    <button type="button" class="row tappable" data-action="toggle-plate" data-id="${p.id}" aria-expanded="${history ? 'true' : 'false'}">
      <span class="row-icon ${tone}"><ion-icon name="${icon}"></ion-icon></span>
      <span class="row-main">
        <span class="row-title">${p.label}</span>
        <span class="row-sub">${requestText(p)}</span>
        ${p.count > 1 ? html`<span class="row-meta">${p.count} pedidos</span>` : ''}
      </span>
      <ion-icon class="chevron" name="${history ? 'chevron-up' : 'chevron-down'}"></ion-icon>
    </button>
    ${history
      ? html`
        <div class="plate-history">
          ${history.loading ? skeletonRows(SKELETON_HISTORY) : ''}
          ${history.error ? html`<p class="panel-text">${history.error}</p>` : ''}
          ${(history.items ?? []).map((r) => html`
            <div class="row">
              <span class="row-main">
                <span class="row-title">Pedido de ${fmtDateTime(r.createdAt)}</span>
                <span class="row-sub ${REQUEST_TONE[r.status] ?? ''}">${requestText(r)}</span>
              </span>
              ${r.status === 'PENDING' ? html`<ion-button size="small" fill="clear" color="danger" data-action="cancel-request" data-id="${r.id}">Cancelar</ion-button>` : ''}
            </div>`)}
        </div>`
      : ''}`;
}

customElements.define('page-workshop-vehicles', WorkshopVehiclesPage);
